import { cn } from '#app/utils/misc.tsx'

/**
 * The morphing icon primitive (plan §1.4, OykaApp2 `bars()`): every icon is
 * exactly three 16×2.6px bars in a 24px box. A state is a set of
 * (x, y, angle, length) per bar; switching states is one 320ms transform
 * transition, so ＋ becomes ×, an open ring becomes ✓ and × becomes ‹.
 */
export type MorphState = 'plus' | 'cross' | 'check' | 'chevR' | 'back' | 'none'

type Bar = { x: number; y: number; a: number; l: number; o?: number }

const Z: Bar = { x: 12, y: 12, a: 0, l: 0, o: 0 }

export const BARS: Record<MorphState, [Bar, Bar, Bar]> = {
	plus: [{ x: 12, y: 12, a: 0, l: 1 }, { x: 12, y: 12, a: 90, l: 1 }, Z],
	cross: [{ x: 12, y: 12, a: 45, l: 1 }, { x: 12, y: 12, a: -45, l: 1 }, Z],
	check: [
		{ x: 8.6, y: 13.6, a: 45, l: 0.42 },
		{ x: 13.6, y: 11.6, a: -50, l: 0.78 },
		Z,
	],
	none: [Z, Z, Z],
	chevR: [
		{ x: 12, y: 9.3, a: 45, l: 0.5 },
		{ x: 12, y: 14.7, a: -45, l: 0.5 },
		Z,
	],
	back: [
		{ x: 12, y: 12, a: 0, l: 0.9 },
		{ x: 8, y: 9.4, a: -45, l: 0.48 },
		{ x: 8, y: 14.6, a: 45, l: 0.48 },
	],
}

export type MorphBarStyle = {
	left: string
	top: string
	transform: string
	opacity: number
}

const px = (n: number) => `${Math.round(n * 100) / 100}px`

/** The bar math of the design: left = x − 8, top = y − 1.3, rotate + scaleX. */
export function morphBars(state: MorphState): MorphBarStyle[] {
	return BARS[state].map((b) => ({
		left: px(b.x - 8),
		top: px(b.y - 1.3),
		transform: `rotate(${b.a}deg) scaleX(${b.l})`,
		opacity: b.o ?? 1,
	}))
}

export function MorphIcon({
	state,
	size = 24,
	className,
}: {
	state: MorphState
	/** rendered box in px; the bars scale from the 24px design box */
	size?: number
	className?: string
}) {
	return (
		<span
			className={cn('oy-morph', className)}
			aria-hidden="true"
			style={size !== 24 ? { scale: `${size / 24}` } : undefined}
		>
			{morphBars(state).map((b, i) => (
				<span key={i} className="oy-morph-bar" style={b} />
			))}
		</span>
	)
}
